import styled from '@emotion/styled'
import React from 'react'

import AcceptedSubsidies from './AcceptedSubsidies'
import Ages from './Ages'
import BackgroundCheck from './BackgroundCheck'
import DaysOfWeek from './DaysOfWeek'
import DaysPerWeek from './DaysPerWeek'
import EnabledProgramOfferings from './EnabledProgramOfferings'
import Hours from './Hours'
import Meals from './Meals'
import NetworkRating from './NetworkRating'
import NetworkRatingV2 from './NetworkRatingV2'
import PaymentSchedules from './PaymentSchedules'
import Philosophy from './Philosophy'
import PottyTraining from './PottyTraining'
import ProfileFeature from './ProfileFeature'
import ProviderRatio from './ProviderRatio'
import Snacks from './Snacks'
import SpokenLanguages from './SpokenLanguages'
import Subsidized from './Subsidized'

import { useWhitelabel } from 'main/framework'
import { SchoolPage_school } from 'main/pages/School/gql'
import { breakpoints, spacing } from 'main/ui'
import { AssetName } from 'main/util/asset'

const InternalBasicsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing.majorScale(1)}px 0;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    flex-wrap: wrap;
    padding: ${spacing.majorScale(2)}px 0 ${spacing.majorScale(1)}px 0;
  }

  > * {
    flex: 1;
    margin: ${spacing.minorScale(3)}px 0;

    @media (min-width: ${breakpoints.lg}px) {
      flex: unset;
      width: 50%;
    }
  }
`
interface IProps {
  school: SchoolPage_school
}

export default function InternalBasics(props: IProps) {
  const { school } = props
  const { profile, licenseNumber, licenseStatus, tuitionBillingPolicy } = school
  const { weeklyBillingAccepted } = tuitionBillingPolicy
  if (!profile) return null
  const {
    philosophy,
    philosophySecondary,
    ageMonthsMax,
    ageMonthsMin,
    daysOfWeek,
    daysPerWeek,
    hoursEnd,
    hoursStart,
    mealsBreakfastProvided,
    mealsLunchProvided,
    mealsDinnerProvided,
    providerChildRatioConsequent,
    snacksProvidedCount,
    pottyTrainingRequired,
    subsidizedCareAccepted,
    spokenLanguages,
    features,
    networkRating,
    whitelabelRating,
    subsidies,
    programOfferings,
  } = profile

  const whitelabel = useWhitelabel()

  const profileFeatures = features.map((feature, i) => (
    <ProfileFeature key={i} profileFeature={feature} />
  ))

  return (
    <InternalBasicsContainer>
      {licenseNumber && licenseStatus && (
        <BackgroundCheck licenseNumber={licenseNumber} licenseStatus={licenseStatus} />
      )}
      {ageMonthsMax && <Ages ageMonthsMax={ageMonthsMax} ageMonthsMin={ageMonthsMin || 0} />}
      {philosophy && philosophy !== 'hidden' && (
        <Philosophy philosophy={philosophy} philosophySecondary={philosophySecondary} />
      )}
      {hoursEnd && hoursStart && <Hours hoursEnd={hoursEnd} hoursStart={hoursStart} />}
      {daysOfWeek.some(day => day) && <DaysOfWeek daysOfWeek={daysOfWeek} />}
      {daysPerWeek && <DaysPerWeek daysPerWeek={daysPerWeek} />}
      {providerChildRatioConsequent && <ProviderRatio ratio={providerChildRatioConsequent} />}
      <PaymentSchedules weeklyBillingAccepted={weeklyBillingAccepted} />
      {(mealsBreakfastProvided || mealsLunchProvided || mealsDinnerProvided) && (
        <Meals
          breakfast={mealsBreakfastProvided}
          lunch={mealsLunchProvided}
          dinner={mealsDinnerProvided}
        />
      )}
      {snacksProvidedCount !== null && <Snacks snacksProvidedCount={snacksProvidedCount} />}
      <PottyTraining pottyTrainingRequired={pottyTrainingRequired} />
      <Subsidized subsidizedCareAccepted={subsidizedCareAccepted} />
      {whitelabel.hideByDefaultExceptIfTrue(AssetName.NETWORK_RATING_DISPLAY__DETAIL__BASICS) && (
        <NetworkRating networkRating={networkRating} />
      )}
      <SpokenLanguages spokenLanguages={spokenLanguages} />
      {whitelabelRating &&
        whitelabel.hideByDefaultExceptIfTrue(AssetName.NETWORK_RATING_FILTERS_V2) && (
          <NetworkRatingV2 whitelabelRating={whitelabelRating} />
        )}

      {whitelabel.hideByDefaultExceptIfTrue(AssetName.SUBSIDY_BUDGET_FILTER) && (
        <AcceptedSubsidies subsidies={subsidies} />
      )}

      <EnabledProgramOfferings programOfferings={programOfferings} />

      {profileFeatures}
    </InternalBasicsContainer>
  )
}
